import * as React from "react";
import { toast } from "sonner";
import { Pin, Bookmark } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import DetailDrawer from "@/components/DetailDrawer";
import EntityAvatar from "@/components/identity/EntityAvatar";

function fmt(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * Pins + Saved items — a two-tab drawer. Pins are scoped to the active
 * channel; Saved items are per-user across the whole company.
 */
export default function PinsSavedDrawer({ open, onOpenChange, companyId, conversation, initialTab = "pins", onJump }) {
  const [tab, setTab] = React.useState(initialTab);
  const [pins, setPins] = React.useState([]);
  const [saved, setSaved] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => { setTab(initialTab); }, [initialTab, open]);

  const load = React.useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        conversation?.id
          ? api.get(`/companies/${companyId}/comms/conversations/${conversation.id}/pins`)
          : Promise.resolve({ data: { pins: [] } }),
        api.get(`/companies/${companyId}/comms/saved`),
      ]);
      setPins(p.data.pins || []);
      setSaved(s.data.saved || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load");
    } finally { setLoading(false); }
  }, [companyId, conversation?.id, open]);

  React.useEffect(() => { load(); }, [load]);

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={tab === "pins" ? "Pinned in this channel" : "Your saved items"}
      subtitle={tab === "pins"
        ? (conversation?.slug ? `in #${conversation.slug}` : "current conversation")
        : "across the whole company"}
      testid="comms-pins-saved-drawer"
    >
      <div className="px-5 pt-3 flex items-center gap-1 border-b border-brand-rule">
        <button
          type="button"
          onClick={() => setTab("pins")}
          className={`chip mono-num text-[11px] ${tab === "pins" ? "chip-violet" : ""}`}
          data-testid="pins-saved-tab-pins"
          role="tab" aria-selected={tab === "pins"}
        >
          <Pin size={11} /> Pinned {conversation?.slug ? `· ${pins.length}` : ""}
        </button>
        <button
          type="button"
          onClick={() => setTab("saved")}
          className={`chip mono-num text-[11px] ${tab === "saved" ? "chip-violet" : ""}`}
          data-testid="pins-saved-tab-saved"
          role="tab" aria-selected={tab === "saved"}
        >
          <Bookmark size={11} /> Saved · {saved.length}
        </button>
      </div>
      {loading ? (
        <div className="p-5 text-[12.5px] text-brand-muted">Loading…</div>
      ) : tab === "pins" ? (
        pins.length === 0 ? (
          <div className="p-6 text-[12.5px] text-brand-muted" data-testid="pins-empty">
            Nothing pinned yet. Managers can pin any message.
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {pins.map((m) => (
              <MessageRow key={m.id} msg={m} onJump={() => { onJump && onJump(m.conversation_id); onOpenChange(false); }} />
            ))}
          </div>
        )
      ) : (
        saved.length === 0 ? (
          <div className="p-6 text-[12.5px] text-brand-muted" data-testid="saved-empty">
            You haven't saved anything yet. Use the bookmark on any message.
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {saved.map((s) => (
              <MessageRow
                key={s.message?.id} msg={s.message}
                extra={<span className="mono-num text-[10px] text-brand-muted">saved {fmt(s.saved_at)}</span>}
                onJump={() => { onJump && onJump(s.conversation_id); onOpenChange(false); }}
              />
            ))}
          </div>
        )
      )}
    </DetailDrawer>
  );
}

function MessageRow({ msg, onJump, extra }) {
  if (!msg) return null;
  return (
    <div className="card-panel p-3 hover:bg-brand-hush cursor-pointer" onClick={onJump}
         data-testid={`pins-saved-row-${msg.id}`}>
      <div className="flex items-center gap-2">
        <EntityAvatar kind="person" name={msg.sender_name || "?"} id={msg.sender_id || msg.sender_name} size={22} />
        <div className="text-[12.5px] font-semibold text-brand-ink truncate">{msg.sender_name}</div>
        <div className="mono-num text-[10.5px] text-brand-muted">{fmt(msg.created_at)}</div>
        <div className="flex-1" />
        {extra}
      </div>
      <div className="text-[12.5px] text-brand-ink whitespace-pre-wrap break-words mt-1">
        {msg.deleted ? <span className="italic text-brand-faint">[deleted]</span> : (msg.body || "").slice(0, 400)}
      </div>
    </div>
  );
}
